import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, RotateCcw, Layers, Edit3 } from "lucide-react";

export default function AdminModulesEditor() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  async function load() {
    setLoading(true);
    const { data } = await api.get("/modules");
    setModules(data);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function setDraft(id, field, value) {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  }

  async function save(id) {
    const draft = drafts[id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await api.put(`/admin/modules/${id}`, draft);
      setDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
      await load();
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  }

  async function reset(id) {
    if (!window.confirm(`Reset Module ${id} metadata to defaults?`)) return;
    await api.post(`/admin/modules/${id}/reset`);
    setDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
    await load();
  }

  if (loading) return <div className="text-neutral-500 py-10 text-center">Loading modules…</div>;

  return (
    <div className="space-y-4" data-testid="admin-modules-editor">
      {modules.map((m) => {
        const draft = drafts[m.id] || {};
        const val = (field) => draft[field] !== undefined ? draft[field] : m[field];
        const dirty = Object.keys(draft).length > 0;
        return (
          <div key={m.id} className="border border-[var(--dw-navy)]/12 rounded-sm p-5 bg-white" data-testid={`m-row-${m.id}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-sm border border-[var(--dw-navy)]/12 flex items-center justify-center">
                <Layers className="w-4 h-4 text-[var(--dw-navy)]" strokeWidth={1.5} />
              </div>
              <div className="overline text-neutral-500">Module {m.id}</div>
              <div className="ml-auto flex gap-1">
                <button onClick={() => reset(m.id)} title="Reset to default" data-testid={`m-reset-${m.id}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-amber-500 rounded-sm text-neutral-500 hover:text-amber-600">
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Title</label>
                <input value={val("title")} onChange={(e) => setDraft(m.id, "title", e.target.value)} data-testid={`m-field-title-${m.id}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Subtitle</label>
                <input value={val("subtitle")} onChange={(e) => setDraft(m.id, "subtitle", e.target.value)} data-testid={`m-field-subtitle-${m.id}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Weight (%)</label>
                <input type="number" value={val("weight") ?? 0} onChange={(e) => setDraft(m.id, "weight", parseInt(e.target.value) || 0)} data-testid={`m-field-weight-${m.id}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Duration (hours)</label>
                <input type="number" value={val("duration_hours") ?? 0} onChange={(e) => setDraft(m.id, "duration_hours", parseInt(e.target.value) || 0)} data-testid={`m-field-duration-${m.id}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Description</label>
                <textarea rows={2} value={val("description") ?? ""} onChange={(e) => setDraft(m.id, "description", e.target.value)} data-testid={`m-field-description-${m.id}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
              </div>
            </div>
            {dirty && (
              <div className="flex justify-end mt-3">
                <button onClick={() => save(m.id)} disabled={saving[m.id]} data-testid={`m-save-${m.id}`} className="dw-btn-gold rounded-sm px-4 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> {saving[m.id] ? "Saving…" : "Save changes"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
